import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import clsx from 'clsx';

//hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import { useTransformStringOnOffset } from '@/utils/hooks/format/useTransformString';
import useAwsTranslate from '@/utils/hooks/language/useAwsTranslate';
import useToggle from '@/utils/hooks/utils/useToggle';

//Lib
import { POST, STORY } from '@/lib/posts/postTypes';
import { FACEBOOK } from '@/lib/socialProfile';

// Utils

// Components
import Carousel from '@/components/base/Carousel';
import { Img } from '@/components/composite/MediaViewer/PostPreview/Img';
import { Item } from '@/components/composite/MediaViewer/PostPreview/Item';
import Icon from '@/components/base/Icon';
import AwsTranslateSameLanguageBanner from '@/components/composite/Banners/AwsTranslateSameLanguageBanner';

export const DefaultPreview = ({
    post,
    postType,
    isLoading,
    remoteMedia,
    repostEmail,
    network,
    ...restProps
}) => {
    const [videoPlayedId, setVideoPlayedId] = useState(null);

    if (isLoading) {
        return (
            <>
                <span className="square block animate-pulse rounded-md bg-icoGray-300 dark:bg-icoDarkMode-deepBlack" />
                <div className="mb-2 mt-8 block w-full rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-2 block w-10/12 rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-2 block w-3/4 rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-2py-1 block w-full rounded bg-icoGray-300 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-2 block w-full rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-3 block w-1/2 rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="block w-1/6 rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-2 block w-full rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-2 block w-10/12 rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-2 block w-3/4 rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-2py-1 block w-full rounded bg-icoGray-300 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-2 block w-full rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="mb-3 block w-1/2 rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="block w-1/6 rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-deepBlack"></div>
            </>
        );
    }

    return (
        <>
            <div className="mb-8">
                {post?.medias?.length > 0 && (
                    <>
                        {post?.medias?.length > 1 ? (
                            <Carousel
                                config={{ slidesToScroll: 1 }}
                                hasDots
                                navPosition="external"
                                navOnHover={true}
                                navCallback={() => {
                                    if (videoPlayedId !== null) {
                                        setVideoPlayedId(null);
                                    }
                                }}
                                aria-label="default-carousel">
                                {post?.medias?.map((media, index) => (
                                    <Carousel.Slide key={index} size="full">
                                        <Item
                                            post={post}
                                            postType={postType}
                                            mediaId={index}
                                            media={media}
                                            videoPlayedId={videoPlayedId}
                                            remoteMedia={remoteMedia}
                                            setVideoPlayedId={setVideoPlayedId}
                                            network={network}
                                            {...restProps}
                                        />
                                    </Carousel.Slide>
                                ))}
                            </Carousel>
                        ) : post?.medias[0]?.type !== 'link' ? (
                            <Item
                                post={post}
                                postType={postType}
                                mediaId={0}
                                media={post?.medias[0]}
                                videoPlayedId={videoPlayedId}
                                remoteMedia={remoteMedia}
                                repostEmail={repostEmail}
                                setVideoPlayedId={setVideoPlayedId}
                                aria-label="default-media"
                                network={network}
                                {...restProps}
                            />
                        ) : null}
                    </>
                )}
            </div>
            <Status post={post} remoteMedia={remoteMedia} />
        </>
    );
};

DefaultPreview.propTypes = {
    post: PropTypes.object,
    postType: PropTypes.oneOf([POST, STORY]),
    isLoading: PropTypes.bool,
    remoteMedia: PropTypes.bool
};

export const Status = ({ post, remoteMedia = false, className }) => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const { isOpen: open, toggle } = useToggle();
    const { t } = useTranslation('commons');
    const { formatTextWithComponent, OffsetReplace, formatFacebookText } =
        useTransformStringOnOffset();

    const { awsTranslation, awsToggleTranslation } = useAwsTranslate(
        activeSocialProfile?.social_network_type === FACEBOOK
            ? formatFacebookText(post?.caption, post?.tags)
            : formatTextWithComponent(post?.caption, post?.tags)
    );

    const { isOpen: isOpenAwsTSLBanner, toggle: toggleAwsTSLBanner } = useToggle();

    useEffect(() => {
        if (awsTranslation?.isSameLanguage) {
            toggleAwsTSLBanner();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awsTranslation]);

    useEffect(() => {
        if (isOpenAwsTSLBanner) {
            toggleAwsTSLBanner();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post]);

    if (!post?.caption && post?.medias?.[0]?.type !== 'link') return null;
    if (!open) {
        return (
            <div
                className={clsx(
                    'whitespace-pre-wrap',
                    {
                        'font-GMedium text-sm leading-4':
                            post?.medias?.length > 0 && post?.medias?.[0]?.type !== 'link',
                        'rounded-md bg-icoGray-100 p-16 dark:bg-icoDarkMode-deepBlack':
                            post?.medias?.length === 0 ||
                            (post?.medias?.length > 0 && post?.medias?.[0]?.type === 'link')
                    },
                    className
                )}
                aria-label="default-preview">
                {post?.caption ? (
                    <>
                        <div className="line-clamp-2">
                            <OffsetReplace
                                text={
                                    awsTranslation?.isDisplayed
                                        ? awsTranslation?.translated
                                        : awsTranslation?.original
                                }
                                components={Object.assign(
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                    { p: <p className="inline break-words" />, br: <br /> },
                                    post?.tags?.map((tag, id) => (
                                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                                        <a
                                            key={id}
                                            href={tag.link}
                                            className="text-icoBlue"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        />
                                    ))
                                )}
                            />
                        </div>
                        <div className="mt-1 flex flex-col space-y-1">
                            <button
                                className="inline-flex max-w-max items-center space-x-0.5 text-sm text-icoBlue"
                                onClick={toggle}>
                                <span>{t('view_more')}</span>
                                <Icon icon="chevron-bottom" className="relative top-px text-xs" />
                            </button>
                            <button
                                className={clsx(
                                    'max-w-max font-GMedium text-xs text-icoGray-500 hover:text-black dark:text-icoDarkMode-wolf',
                                    {
                                        'cursor-wait': awsTranslation?.isLoading
                                    }
                                )}
                                disabled={awsTranslation?.isLoading}
                                onClick={() => {
                                    if (!awsTranslation?.isLoading) {
                                        awsToggleTranslation();
                                    }
                                }}
                                data-testid="translate-caption-btn">
                                {t(awsTranslation?.isDisplayed ? 'original_text' : 'translate')}
                            </button>
                        </div>
                        {isOpenAwsTSLBanner ? (
                            <AwsTranslateSameLanguageBanner
                                className="mt-2"
                                onClose={toggleAwsTSLBanner}
                            />
                        ) : null}
                    </>
                ) : null}
                {post?.medias?.length === 1 && post?.medias?.[0]?.type === 'link' ? (
                    <LinkPreview media={post?.medias?.[0]} remoteMedia={remoteMedia} />
                ) : null}
            </div>
        );
    }
    return (
        <div
            className={clsx(
                'whitespace-pre-wrap',
                {
                    'font-GMedium text-sm leading-4':
                        post?.medias?.length > 0 && post?.medias?.[0]?.type !== 'link',
                    'rounded-md bg-icoGray-100 p-16 dark:bg-icoDarkMode-deepBlack':
                        post?.medias?.length === 0 ||
                        (post?.medias?.length > 0 && post?.medias?.[0]?.type === 'link')
                },
                className
            )}
            aria-label="default-preview">
            {post?.caption ? (
                <>
                    <div>
                        <OffsetReplace
                            text={
                                awsTranslation?.isDisplayed
                                    ? awsTranslation?.translated
                                    : awsTranslation?.original
                            }
                            components={Object.assign(
                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                { p: <p />, br: <br /> },
                                post?.tags?.map((tag, id) => (
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                    <a
                                        key={id}
                                        href={tag.link}
                                        className="text-icoBlue"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                ))
                            )}
                        />
                    </div>
                    <div className="mt-1 flex flex-col space-y-1">
                        <button
                            className="inline-flex max-w-max items-center space-x-0.5 text-sm text-icoBlue"
                            onClick={toggle}>
                            <span>{t('view_less')}</span>
                            <Icon icon="chevron-top" className="relative top-px text-xs" />
                        </button>
                        <button
                            className={clsx(
                                'max-w-max font-GMedium text-xs text-icoGray-500 hover:text-black dark:text-icoDarkMode-wolf',
                                {
                                    'cursor-wait': awsTranslation?.isLoading
                                }
                            )}
                            disabled={awsTranslation?.isLoading}
                            onClick={() => {
                                if (!awsTranslation?.isLoading) {
                                    awsToggleTranslation();
                                }
                            }}
                            data-testid="translate-caption-btn">
                            {t(awsTranslation?.isDisplayed ? 'original_text' : 'translate')}
                        </button>
                    </div>
                    {isOpenAwsTSLBanner ? (
                        <AwsTranslateSameLanguageBanner
                            className="mt-2"
                            onClose={toggleAwsTSLBanner}
                        />
                    ) : null}
                </>
            ) : null}
            {post?.medias?.length === 1 && post?.medias?.[0]?.type === 'link' ? (
                <LinkPreview media={post?.medias?.[0]} remoteMedia={remoteMedia} />
            ) : null}
        </div>
    );
};

Status.propTypes = {
    post: PropTypes.object,
    remoteMedia: PropTypes.bool,
    className: PropTypes.string
};

const LinkPreview = ({ media, remoteMedia }) => {
    return (
        <a href={media.target} target="_blank" rel="noopener noreferrer" aria-label="facebook-link">
            {media.image && (
                <div className="facebook-preview-link relative w-full overflow-hidden rounded-t-md border border-icoGray-300">
                    <Img
                        postId=""
                        mediaId=""
                        src={media.image}
                        remoteMedia={remoteMedia}
                        imgFit="cover"
                    />
                </div>
            )}
            <div
                className={clsx(
                    'flex items-center justify-between space-x-3 border border-icoGray-300 bg-white p-5 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite',
                    {
                        'rounded-b-md border-t-0': media?.image,
                        'rounded-md': !media?.image
                    }
                )}>
                <div className="flex flex-col space-y-1 overflow-hidden">
                    <span className="truncate whitespace-nowrap text-xxs">{media?.title}</span>
                    <p className="flex items-center text-icoGray-500 dark:text-icoDarkMode-wolf">
                        <Icon icon="link" className="text-xxs" />
                        <span className="ml-2 text-xxs">{media?.target}</span>
                    </p>
                </div>
                <Icon
                    icon="chevron-right"
                    className="text-xs text-icoGray-500 dark:text-icoDarkMode-wolf"
                />
            </div>
        </a>
    );
};

export default Status;
